import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { XIcon } from '@/components/icons';
import { getSignedMediaUrl } from '@/lib/utils';
import type { AddOnResponse } from '@/features/cart/types';
import { formatCurrencyForDisplay } from '@/lib/utils';

interface AddOnDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  addOn: AddOnResponse | null;
  totalPrice: number;
  guestsWithAddOn: number[];
  pricePerGuest: number;
  description?: string;
  quantitiesPerGuest?: number[]; // Quantity for each guest that has this add-on
  eventStartTime?: string; // Event schedule start time for early entry calculation
  earlyEntryDuration?: number | null; // Duration in nanoseconds
  currency: string;
}

const AddOnDetailModal: React.FC<AddOnDetailModalProps> = ({
  isOpen,
  onClose,
  addOn,
  totalPrice,
  guestsWithAddOn,
  pricePerGuest,
  description,
  quantitiesPerGuest,
  eventStartTime,
  earlyEntryDuration,
  currency
}) => {
  const formatPrice = (amount: number) => formatCurrencyForDisplay(amount, currency);

  if (!isOpen || !addOn) return null;

  const hasValidImage = addOn.image_id && addOn.image_id !== '00000000-0000-0000-0000-000000000000';
  const imageUrl = hasValidImage ? getSignedMediaUrl(addOn.image_id, 'image').url : null;

  // Calculate total quantity across all guests
  const totalQuantity = quantitiesPerGuest?.reduce((sum, qty) => sum + qty, 0) || guestsWithAddOn.length;

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl max-h-[90vh] bg-gray-900 rounded-2xl border border-gray-700/50 overflow-hidden shadow-2xl flex flex-col"
            style={{
              background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.98), rgba(31, 41, 55, 0.98))',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
            }}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-2 bg-gray-800/80 hover:bg-gray-700/80 rounded-full text-gray-300 hover:text-white transition-all duration-200 backdrop-blur-sm"
            >
              <XIcon className="w-5 h-5" />
            </button>

            {/* Image Header */}
            <div className="relative h-64 overflow-hidden flex-shrink-0">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: imageUrl
                    ? `url(${imageUrl})`
                    : 'linear-gradient(135deg, #1f2937 0%, #374151 100%)'
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
              
              {/* Name and Price Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-3xl font-bold text-white mb-2">{addOn.name}</h3>
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">{formatPrice(pricePerGuest)} per item</span>
                  <span className="text-2xl font-bold text-blue-400">{formatPrice(totalPrice)}</span>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* Description */}
              {(description || addOn.description) && (
                <div>
                  <h4 className="text-lg font-semibold text-white mb-2">Description</h4>
                  <p className="text-gray-300 leading-relaxed">
                    {description || addOn.description}
                  </p>
                </div>
              )}

              {/* Early Entry Banner - Show when addon is "Early Entry" and is_platform_add_on is true */}
              {addOn.name === 'Early Entry' && addOn.is_platform_add_on && eventStartTime && earlyEntryDuration && (
                <div className="bg-gradient-to-r from-emerald-900/40 to-teal-900/40 border border-emerald-500/50 rounded-lg p-4 shadow-lg">
                  <div className="flex items-start space-x-3">
                    <div className="text-emerald-400 text-2xl">🎟️</div>
                    <div>
                      <h5 className="text-emerald-300 font-bold text-lg mb-1">You can take entry early!</h5>
                      <p className="text-emerald-100 text-sm">
                        Earliest entry time:{' '}
                        <span className="font-bold text-white">
                          {new Date(new Date(eventStartTime).getTime() - (earlyEntryDuration / 1_000_000)).toLocaleTimeString('en-US', { 
                            hour: '2-digit', 
                            minute: '2-digit', 
                            hour12: true 
                          })}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Price Breakdown */}
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                <h4 className="text-lg font-semibold text-white mb-3">Price Breakdown</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-gray-300">
                    <span>Price per item</span>
                    <span>{formatPrice(pricePerGuest)}</span>
                  </div>
                  <div className="flex justify-between text-gray-300">
                    <span>Total quantity</span>
                    <span>{totalQuantity}</span>
                  </div>
                  <div className="pt-2 border-t border-gray-700/50 flex justify-between text-white font-semibold">
                    <span>Total</span>
                    <span>{formatPrice(totalPrice)}</span>
                  </div>
                </div>
              </div>

              {/* Guests with Add-on */}
              <div>
                <h4 className="text-lg font-semibold text-white mb-2">Guests with this Add-on</h4>
                <div className="flex flex-wrap gap-2">
                  {guestsWithAddOn.map((guestNum, index) => {
                    const quantity = quantitiesPerGuest?.[index] || 1;
                    return (
                      <span
                        key={guestNum}
                        className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm font-medium border border-blue-500/30"
                      >
                        Guest {guestNum}{quantity > 1 && <span className="ml-1 text-blue-400">×{quantity}</span>}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default AddOnDetailModal;

